import React, { useEffect, useState } from 'react';
import { getDashboardDrilldown, DashboardDrilldownRow } from '../../../../services/salesAnalyticsApi';
import styles from './DrilldownModal.module.css';

interface DrilldownModalProps {
  title: string;
  onClose: () => void;
}

function fmtMoney(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

/**
 * VP-ADM-001 — click-through behind a Dashboard KPI card.
 * Backed by GET /admin/dashboard/drilldown: the individual this-month orders
 * (with per-order computed margin) that back the current KPI totals.
 */
export const DrilldownModal: React.FC<DrilldownModalProps> = ({ title, onClose }) => {
  const [rows, setRows] = useState<DashboardDrilldownRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getDashboardDrilldown(100)
      .then(setRows)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load drilldown'));
  }, []);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <h3 className={styles.title}>{title}</h3>
            <p className={styles.subtitle}>Orders backing this month's totals, newest first</p>
          </div>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className={styles.body}>
          {error && <div className={styles.empty}>{error}</div>}
          {!error && rows === null && <div className={styles.loading}>Loading…</div>}
          {!error && rows !== null && rows.length === 0 && (
            <div className={styles.empty}>No orders this month yet.</div>
          )}
          {!error && rows !== null && rows.length > 0 && (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th className={styles.right}>Revenue</th>
                  <th className={styles.right}>Cost</th>
                  <th className={styles.right}>Margin</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.order_id}>
                    <td>#{r.order_id}</td>
                    <td>{r.customer_name}</td>
                    <td>{new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                    <td className={styles.right}>{fmtMoney(r.total_amount)}</td>
                    <td className={styles.right}>{fmtMoney(r.cost)}</td>
                    <td className={`${styles.right} ${r.margin_percent >= 0 ? styles.marginPositive : styles.marginNegative}`}>
                      {r.margin_percent}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
